import { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep } from 'es-toolkit';
import PropTypes from 'prop-types';

import { AdornedInput, FormField, FormHelp, Label } from '@zillow/constellation-10';

import PropertiesActions from '../../../actions/PropertiesActions';
import actions from '../../../containers/CreateListingPage/actions';
import { useForm, useGuardedMultiEdit } from '../../../hooks';
import { AppSelectors } from '../../../selectors';
import { postingConstraints } from '../../../utils/formValidation';
import { multiFieldsUtil, SAVE_TO_PARENT_KEY, UPDATE_CURRENT_PROPERTY_KEY } from '../../../utils/postingUtils';
import wizardActions from '../../Wizard/actions';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';
import WizardServerError from '../../../model/WizardServerError';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';

import { WizardContext } from '../../Wizard/context';
import WizardAlert from '../../WizardAlert';

const propTypes = {
  dataTestId: PropTypes.string,
  inputName: PropTypes.string.isRequired,
  label: PropTypes.string,
  passNullWhenEmptyString: PropTypes.bool,
  placeholder: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  castToDataType: PropTypes.string,
  floorPlanId: PropTypes.string,
  required: PropTypes.bool,
  rightAdornmentContent: PropTypes.string,
  // the parent key where the data will be saved to via multi endpoint
  saveToParentKey: PropTypes.string,
  // the key that determines how to update current property
  updateCurrentPropertyKey: PropTypes.string,
  // a function that is used to check an additional condition outside of formValidation
  isExternalConditionValid: PropTypes.func,
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func,
};

const defaultProps = {
  dataTestId: 'TextAreaFormField',
  label: 'Label',
  passNullWhenEmptyString: false,
  placeholder: 'placeholder',
  initialValue: '',
  castToDataType: null,
  floorPlanId: null,
  required: false,
  rightAdornmentContent: null,
  saveToParentKey: null,
  updateCurrentPropertyKey: null,
  errorObj: null,
  refClearFieldError: null,
  isExternalConditionValid: null,
  handleBlur: null,
  handleChange: null,
};

const TextFormField = ({
  dataTestId,
  inputName,
  label,
  passNullWhenEmptyString,
  placeholder,
  initialValue,
  castToDataType,
  floorPlanId,
  required,
  rightAdornmentContent,
  saveToParentKey,
  updateCurrentPropertyKey,
  // allows an override the local state of errorObj
  errorObj: propErrorObj,
  refClearFieldError,
  isExternalConditionValid,
  handleBlur: propHandleBlur,
  handleChange: propHandleChange,
}) => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, saveOutsideComponent, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    properties: {
      currentProperty: { listingAlias, floorplans, roomForRent },
    },
  } = useSelector((state) => state);
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const prevValue = useRef();

  const {
    clearErrorObj,
    inputs,
    errorObj: formErrorObj,
    hasFieldError,
    handleChange: handleFormChange,
    addErrorKey,
  } = useForm(
    {
      [inputName]: initialValue,
    },
    postingConstraints,
  );

  const errorObj = propErrorObj !== null ? propErrorObj : formErrorObj;

  const { saveMultipleFieldsPropertyInfo } = useGuardedMultiEdit();

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: inputName,
          value: inputs[inputName],
        },
      });
    }
  }, [isSaveComplete]);

  useEffect(() => {
    setClearFieldErrorRef();

    updatePrevValueRef(initialValue);
  }, []);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const clearFieldError = () => {
    clearErrorObj();
    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });
  };

  const hasConditionalError = () => {
    let response = false;

    if (isExternalConditionValid) {
      const { response: aResponse, message } = isExternalConditionValid();

      if (aResponse === false) {
        addErrorKey(inputName, message);

        response = true;
      }
    }

    return response;
  };

  const setClearFieldErrorRef = () => {
    if (refClearFieldError) {
      refClearFieldError.current = clearFieldError;
    }
  };

  const save = (event) => {
    if (propHandleBlur) {
      propHandleBlur(event);
    }

    const { value, name: fieldName } = event.target;

    const isValueChanging = value !== prevValue.current;

    if (hasFieldError(fieldName, value) || hasConditionalError()) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return;
    }

    if (isValueChanging === false && saveOutsideComponent === false) {
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      return;
    }

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    let valueToSave = value;

    if (value) {
      if (castToDataType === 'Number') {
        valueToSave = Number(valueToSave);
      }
    }

    if (value === '' && passNullWhenEmptyString) {
      valueToSave = null;
    }

    // saveOutsideComponent check must be above updateIsFetchingData to keep next and prev functionality
    if (saveOutsideComponent) {
      actions.addFieldDataEntry({ reducerDispatch, entryObj: { saveToParentKey, fieldName, value: valueToSave } });

      return;
    }

    const dataToSave = multiFieldsUtil.getDataToSave({
      fieldName,
      floorPlanId,
      listingAlias,
      saveToParentKey,
      value: valueToSave,
    });

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    return saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, fieldName);
          return;
        }
        updateCurrentProperty({ valueToSave });

        updatePrevValueRef(value);

        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err) => {
        updateServerError(err?.message ?? '', fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateCurrentProperty = ({ valueToSave }) => {
    if (updateCurrentPropertyKey && updateCurrentPropertyKey === UPDATE_CURRENT_PROPERTY_KEY.ROOMMATE_REQUIREMENTS) {
      dispatch(
        PropertiesActions.updateCurrentPropertyRoommateRequirements({
          [inputName]: valueToSave,
        }),
      );

      return;
    }

    if (saveToParentKey === SAVE_TO_PARENT_KEY.ROOM_FOR_RENT) {
      dispatch(
        PropertiesActions.updateCurrentProperty({
          roomForRent: {
            ...roomForRent,
            [inputName]: valueToSave,
          },
        }),
      );
    }

    if (saveToParentKey === SAVE_TO_PARENT_KEY.FLOOR_PLANS_ROOM_FOR_RENT) {
      const copyFloorplan = cloneDeep(floorplans);
      copyFloorplan[0]['roomForRent'][inputName] = valueToSave;

      dispatch(PropertiesActions.updateCurrentProperty({ floorplans: copyFloorplan }));
    }
  };

  const updatePrevValueRef = (value) => {
    prevValue.current = value;
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event) => {
    if (propHandleChange) {
      propHandleChange(event);
    }

    clearFieldError();
    clearServerError();
    handleFormChange(event);
  };

  const renderRightAdornment = () => {
    if (rightAdornmentContent) {
      return <AdornedInput.Adornment>{rightAdornmentContent}</AdornedInput.Adornment>;
    }

    return null;
  };

  return (
    <div data-testid={dataTestId}>
      {serverErrorObj.fieldName === inputName && (
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />
      )}

      <FormField
        label={<Label required={required}>{label}</Label>}
        error={!!errorObj[inputName]}
        control={
          <AdornedInput
            size={isScreenDesktop ? 'lg' : 'md'}
            input={
              <AdornedInput.Input
                name={inputName}
                value={inputs[inputName]}
                placeholder={placeholder}
                onChange={handleChange}
                onBlur={save}
                error={!!errorObj[inputName]}
              />
            }
            endAdornment={renderRightAdornment()}
          />
        }
        description={<FormHelp>{errorObj[inputName]}</FormHelp>}
      />
    </div>
  );
};

TextFormField.propTypes = propTypes;
TextFormField.defaultProps = defaultProps;

export default TextFormField;
